import { useRef, useCallback } from 'react';

/**
 * Represents a data update notification
 * @template T The type of the shared data structure
 */
export interface DataUpdateNotification<T> {
  /** The full path of the updated property (dot notation) */
  path: string;
  /** The new value of the updated property */
  value: any;
  /** The complete data object after the update */
  fullData: T;
}

/**
 * Hook for managing shared data between components with a pub/sub notification system
 *
 * @template T Type of the shared data structure
 * @param initialData The initial data structure to use
 * @returns An object containing methods to interact with the shared data state
 *
 * @example
 * // Define your data type
 * interface CalendarData {
 *   events: Array<{ id: string, title: string, start: Date }>;
 *   view: { current: string, available: string[] };
 *   settings: { showWeekends: boolean };
 * }
 *
 * // Use the hook with your data type
 * const { getData, updateData, registerForUpdates } = useSharedData<CalendarData>({
 *   events: [],
 *   view: { current: 'month', available: ['day', 'week', 'month'] },
 *   settings: { showWeekends: true }
 * });
 */
export function useSharedData<T extends Record<string, any>>(initialData: T) {
  // Reference to store the shared data
  const dataRef = useRef<T>({ ...initialData });

  // Registry of notification callbacks
  const notifyCallbacks = useRef<
    Record<string, Array<(data: DataUpdateNotification<T>) => void>>
  >({});

  /**
   * Updates a value in the shared data structure
   *
   * @param keyPath Either a dot-notation path string (e.g., 'view.current') or an object with multiple updates
   * @param value The value to set (only used when keyPath is a string)
   *
   * @example
   * // Update a single nested property
   * updateData('settings.showWeekends', false);
   *
   * // Update multiple properties at once
   * updateData({
   *   'view.current': 'week',
   *   'settings.showWeekends': true
   * });
   */
  const updateData = useCallback(
    (keyPath: string | Record<string, any>, value?: any) => {
      // Helper function to update nested values
      function updateNestedValue(path: string, val: any) {
        const keys = path.split('.');
        let current: any = dataRef.current;

        // Traverse the nested structure until the last key
        for (let i = 0; i < keys.length - 1; i++) {
          if (current[keys[i]] === undefined) {
            // Create the path if it doesn't exist
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        // Update the final key with the new value
        const lastKey = keys[keys.length - 1];
        current[lastKey] = val;

        // Notify all relevant listeners
        notifyAllListeners(path, val);
      }

      // Helper function to notify all relevant listeners
      function notifyAllListeners(path: string, val: any) {
        // Get all segments of the path to notify both direct and parent paths
        const segments = path.split('.');

        // Create all possible parent paths to notify
        const pathsToNotify: string[] = [];
        for (let i = 0; i < segments.length; i++) {
          const parentPath = segments.slice(0, i + 1).join('.');
          pathsToNotify.push(parentPath);
        }

        // Notify all callbacks registered for these paths
        pathsToNotify.forEach((notifyPath) => {
          if (notifyCallbacks.current[notifyPath]) {
            notifyCallbacks.current[notifyPath].forEach((cb) => {
              // Pass the updated value and full path to the callback
              cb({
                path,
                value: val,
                fullData: dataRef.current,
              });
            });
          }
        });
      }

      // If keyPath is an object, handle batch updates (multiple key-value pairs)
      if (typeof keyPath === 'object' && keyPath !== null) {
        const updates = keyPath;
        Object.entries(updates).forEach(([k, v]) => {
          updateNestedValue(k, v);
        });
      } else if (typeof keyPath === 'string') {
        // Handle single nested key-value pair (e.g., 'bookedSlot.duration')
        updateNestedValue(keyPath, value);
      } else {
        console.error('Invalid keyPath type provided to updateData');
      }
    },
    []
  );

  /**
   * Registers a callback to be notified when specific properties change
   *
   * @param properties A string, array, or comma-separated list of property paths to watch
   * @param callback The function to call when any of the specified properties change
   * @returns A function to unregister the listener
   *
   * @example
   * // Register for updates on a single property
   * const unsubscribe = registerForUpdates('events', (update) => {
   *   console.log('Events updated:', update.value);
   * });
   *
   * // Register for updates on multiple properties
   * registerForUpdates(['view.current', 'settings'], (update) => {
   *   console.log(`${update.path} changed to:`, update.value);
   * });
   *
   * // Unsubscribe when done
   * unsubscribe();
   */
  const registerForUpdates = useCallback(
    (
      properties: string | string[],
      callback: (data: DataUpdateNotification<T>) => void
    ) => {
      // Handle different types of property inputs
      let propsToRegister: string[] = [];

      if (typeof properties === 'string') {
        // Handle string that might be comma-separated or look like an array string
        if (properties.startsWith('[') && properties.endsWith(']')) {
          // Handle '[prop1, prop2]' format
          const trimmed = properties.substring(1, properties.length - 1);
          propsToRegister = trimmed.split(',').map((p) => p.trim());
        } else if (properties.includes(',')) {
          // Handle 'prop1, prop2' format
          propsToRegister = properties.split(',').map((p) => p.trim());
        } else {
          // Single property string
          propsToRegister = [properties];
        }
      } else if (Array.isArray(properties)) {
        // Already an array of properties
        propsToRegister = properties;
      } else {
        // This case should be unreachable with proper TypeScript usage
        // but adding it for safety and to fix the type error
        propsToRegister = [`${properties}`]; // Convert to string safely
      }

      const unregisterFns = propsToRegister.map((prop) => {
        if (!notifyCallbacks.current[prop]) {
          notifyCallbacks.current[prop] = [];
        }
        notifyCallbacks.current[prop].push(callback);

        return () => {
          notifyCallbacks.current[prop] = notifyCallbacks.current[prop].filter(
            (cb) => cb !== callback
          );
          if (notifyCallbacks.current[prop].length === 0) {
            delete notifyCallbacks.current[prop];
          }
        };
      });

      // Return a combined unregister function
      return () => unregisterFns.forEach((unregister) => unregister());
    },
    []
  );

  /**
   * Gets the current complete data object or a specific property
   *
   * @param path Optional dot-notation path to get a specific property
   * @returns The current data or specific property value
   *
   * @example
   * // Get the entire data structure
   * const allData = getData();
   *
   * // Get a specific nested property
   * const currentView = getData('view.current');
   */
  const getData = useCallback((path?: string) => {
    if (!path) {
      return dataRef.current;
    }

    const keys = path.split('.');
    let value: any = dataRef.current;

    // Traverse the path
    for (let i = 0; i < keys.length; i++) {
      if (value === undefined || value === null) {
        return undefined;
      }
      value = value[keys[i]];
    }

    return value;
  }, []);

  return {
    /** Get the current data or a specific property */
    getData,
    /** Update data with a path and value or batch update with an object */
    updateData,
    /** Subscribe to changes on specific properties */
    registerForUpdates,
    /** Reference to the raw data object (use with caution) */
    dataRef,
  };
}
